/**
 * Phira compatibility rules PH-01 ... PH-39 except the text-segment rules PH-03, PH-15 and PH-16 (text.rules.ts)
 * (W2-12b checker-rules; DESIGN.md §7.6, §7.2; rpe-format.md F§13; rendering.md R§11; formats-and-packaging.md
 * P§6.4; rpe-editor-features.md E§5).
 *
 * Worker-safe: imports only src/core/** (rules run in the analysis worker on a document replica). Every problem
 * states WHAT is wrong and WHY it matters in Phira, offers fixes (W2-12a's `problems.fix.*` commands or contracted
 * navigation commands) and carries a stable id that doubles as the "Ignore here" key (core/check/geometry.ts).
 * Shared helpers (ids, fixes, geometry, media, easing, shader uniforms, Bezier scan) live in core/check/geometry.ts.
 */

import {
  BUILTIN_SHADER_UNIFORMS,
  FIELD,
  MAX_TEXTURE_SIDE_PX,
  PREVIEW_END_MARGIN_SEC,
  PREVIEW_NULL_END_SEC,
  RESERVED_HITSOUND_NAMES,
  RPE_VISIBLE_LAYERS,
  asciiPath,
  clampedPreviewRange,
  assetPaths,
  browserCanConvert,
  clampBezierHandles,
  contextExtensions,
  controlCurveArg,
  controlField,
  easeArg,
  easeParams,
  emitProblem,
  fixRef,
  fmtBeat,
  fmtNum,
  fmtPoints,
  fmtSec,
  hasNonAscii,
  hasProp,
  isBatchExportMarker,
  isInertCurve,
  isNeutralCurve,
  isSkinTriggerName,
  kindOf,
  layerOf,
  lineLabel,
  lineNotes,
  linesToCheck,
  mediaStatus,
  msg,
  musicLengthSec,
  navFix,
  nudgeBezier,
  parseShaderDefaults,
  problemId,
  propTracks,
  revealFix,
  safePackagePath,
  samePoints,
  scanBezierUses,
  trackKeys,
  uniquePath,
  wholeEase,
  type BezierUse,
  type MediaUse,
  type UniformSpec,
} from '../../../core/check/geometry';
import type { CheckContext, CheckRule, FixRef } from '../../../core/check/types';
import { Ease as EaseApi } from '../../../core/ease/index';
import { resolveExtra } from '../../../core/ease/rpeMap';
import type { Ease, PhiraTweenDesc } from '../../../core/ease/types';
import type { IrEvent, IrExtraKeyframe } from '../../../core/ir/types';
import { isGifAsset } from '../../../core/model/lineKind';
import { LAYERED_PROPS, leadInRule, propAppliesTo } from '../../../core/model/props';
import type {
  AssetId,
  AssetRef,
  Effect,
  EffectId,
  EffectVarType,
  Key,
  KeyId,
  Line,
  LineId,
  LineProp,
  Note,
  NoteId,
  Track,
  UiElement,
  VideoId,
} from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';
import type { Json, MessageRef } from '../../../core/util/types';
import { skewIsDefault } from '../../../formats/rpe/kept';

const fr = Math.fround;

// ---------------------------------------------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------------------------------------------

function label(ctx: CheckContext, line: LineId): string {
  return lineLabel(ctx.doc, ctx.idx, line);
}

function lineOf(ctx: CheckContext, line: LineId): Line | undefined {
  return ctx.doc.lines.get(line);
}

function isUiMoverLine(ctx: CheckContext, line: LineId): boolean {
  return (lineOf(ctx, line)?.attachUI ?? null) !== null;
}

function goTo(beat: Beat, sel: Parameters<typeof revealFix>[2] = {}): FixRef {
  return revealFix(msg('rules.fixGoTo'), beat, sel);
}

/** "Go to <line>": makes a line active, for problems about a whole line (no beat to go to: no "Go" button). */
function goToLine(ctx: CheckContext, line: LineId): FixRef {
  return revealFix(msg('rules.fixGoToLine', { line: label(ctx, line) }), Beats.ZERO, { line });
}

function selectNotes(notes: readonly Note[]): FixRef {
  const first = notes[0];
  return revealFix(
    msg('rules.fixSelectNotes', { count: notes.length }),
    first?.t ?? Beats.ZERO,
    first === undefined ? {} : { line: first.line, notes: notes.map((n) => n.id) },
  );
}

/** Line of a track's owner, null for effect / video variables. */
function trackLine(track: Track): LineId | null {
  return track.owner.kind === 'line' ? track.owner.line : null;
}

function assetLabel(asset: AssetRef | undefined, id: AssetId): string {
  return asset?.path ?? id;
}

// ---------------------------------------------------------------------------------------------------------------
// PH-01 referenced media missing or undecodable
// ---------------------------------------------------------------------------------------------------------------

interface MediaUseGroup {
  readonly asset: AssetId;
  readonly use: MediaUse;
  readonly lines: LineId[];
  readonly notes: Note[];
  readonly effects: EffectId[];
  readonly videos: VideoId[];
  unlockVideo: boolean;
}

function mediaUses(ctx: CheckContext): Map<string, MediaUseGroup> {
  const out = new Map<string, MediaUseGroup>();
  const group = (asset: AssetId, use: MediaUse): MediaUseGroup => {
    const k = `${asset}|${use}`;
    let g = out.get(k);
    if (g === undefined) {
      g = { asset, use, lines: [], notes: [], effects: [], videos: [], unlockVideo: false };
      out.set(k, g);
    }
    return g;
  };
  const { doc } = ctx;
  for (const id of doc.lineOrder) {
    const line = doc.lines.get(id);
    if (line?.texture != null) group(line.texture, 'image').lines.push(id);
  }
  for (const note of doc.notes.values()) {
    if (note.hitsound?.kind === 'asset') group(note.hitsound.asset, 'audio').notes.push(note);
  }
  for (const id of doc.effectOrder) {
    const e = doc.effects.get(id);
    if (e?.shader.kind === 'asset') group(e.shader.asset, 'shader').effects.push(id);
  }
  for (const v of doc.videos.values()) group(v.asset, 'video').videos.push(v.id);
  if (doc.meta.unlockVideo !== null) group(doc.meta.unlockVideo, 'video').unlockVideo = true;
  for (const g of out.values()) g.notes.sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id);
  return out;
}

const USE_FIELD: Readonly<Record<MediaUse, string>> = {
  image: FIELD.lineTexture,
  audio: FIELD.noteHitsound,
  shader: FIELD.effectShader,
  video: FIELD.assetPath,
};

const ph01: CheckRule = {
  id: 'PH-01',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['assets', 'lines', 'notes', 'effects', 'meta'],
  defaultEnabled: true,
  title: msg('rules.ph01Title'),
  check(ctx) {
    for (const g of mediaUses(ctx).values()) {
      const asset = ctx.doc.assets.get(g.asset);
      const status = mediaStatus(asset, g.use);
      if (status.status === 'ok') continue;
      const path = assetLabel(asset, g.asset);
      const users = g.lines.length + g.notes.length + g.effects.length + g.videos.length + (g.unlockVideo ? 1 : 0);
      const fixes: FixRef[] = [navFix('assets.replace', msg('rules.fixChooseFile'), { asset: g.asset })];
      if (status.status === 'undecodable') {
        if (g.use === 'audio' && browserCanConvert(asset, 'audio')) {
          fixes.push(fixRef('problems.fix.convertAudioToWav', msg('rules.fixConvertToWav'), { asset: g.asset }));
        }
        if (g.use === 'image' && browserCanConvert(asset, 'image')) {
          fixes.push(fixRef('problems.fix.convertImageToPng', msg('rules.fixConvertToPng'), { asset: g.asset }));
        }
      }
      fixes.push(
        fixRef('problems.fix.removeAssetReferences', msg('rules.fixRemoveReferences', { count: users }), {
          asset: g.asset,
        }),
      );
      const firstNote = g.notes[0];
      const firstLine = g.lines[0];
      if (firstNote !== undefined) fixes.push(selectNotes(g.notes));
      else if (firstLine !== undefined) fixes.push(goToLine(ctx, firstLine));
      const firstEffect = g.effects[0];
      emitProblem(ctx, {
        id: problemId('PH-01', 'asset', `${g.asset}-${g.use}`),
        message:
          status.status === 'missing'
            ? msg('rules.ph01Missing', { use: g.use, path })
            : msg('rules.ph01Undecodable', { use: g.use, path, codec: status.codec ?? '?' }),
        why: msg('rules.ph01Why', { count: users }),
        at: {
          asset: g.asset,
          field: USE_FIELD[g.use],
          ...(firstLine !== undefined ? { line: firstLine } : firstNote !== undefined ? { line: firstNote.line } : {}),
          ...(g.lines.length > 0 ? { lines: g.lines } : {}),
          ...(g.notes.length > 0 ? { notes: g.notes.map((n) => n.id), beat: firstNote?.t ?? Beats.ZERO } : {}),
          ...(firstEffect !== undefined ? { effect: firstEffect } : {}),
        },
        fixes,
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-02 music missing or undecodable
// ---------------------------------------------------------------------------------------------------------------

const ph02: CheckRule = {
  id: 'PH-02',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['meta', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph02Title'),
  check(ctx) {
    const music = ctx.doc.meta.music;
    if (music === null) {
      emitProblem(ctx, {
        id: problemId('PH-02', 'chart', 'none'),
        message: msg('rules.ph02None'),
        why: msg('rules.ph02Why'),
        at: { field: FIELD.metaMusic },
        fixes: [navFix('assets.add', msg('rules.fixAddMusic'), { role: 'music' })],
      });
      return;
    }
    const asset = ctx.doc.assets.get(music);
    const status = mediaStatus(asset, 'audio');
    if (status.status === 'ok') return;
    const path = assetLabel(asset, music);
    const fixes: FixRef[] = [];
    if (status.status === 'undecodable' && browserCanConvert(asset, 'audio')) {
      fixes.push(fixRef('problems.fix.convertAudioToWav', msg('rules.fixConvertToWav'), { asset: music }));
    }
    fixes.push(navFix('assets.replace', msg('rules.fixChooseMusic'), { asset: music }));
    emitProblem(ctx, {
      id: problemId('PH-02', 'asset', music),
      message:
        status.status === 'missing'
          ? msg('rules.ph02Missing', { path })
          : msg('rules.ph02Undecodable', { path, codec: status.codec ?? '?' }),
      why: msg('rules.ph02Why'),
      at: { asset: music, field: FIELD.metaMusic },
      fixes,
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-04 GIF with zero total frame delay
// ---------------------------------------------------------------------------------------------------------------

/** Lines that Phira plays as animated GIFs (GIF texture with progress keys or "Play automatically"). */
function animatedGifLines(ctx: CheckContext): Map<AssetId, LineId[]> {
  const out = new Map<AssetId, LineId[]>();
  for (const id of ctx.doc.lineOrder) {
    const line = ctx.doc.lines.get(id);
    if (line?.texture == null) continue;
    if (!isGifAsset(ctx.doc.assets.get(line.texture))) continue;
    if (!line.gifAutoLoop && !hasProp(ctx, id, 'gif')) continue;
    const list = out.get(line.texture) ?? [];
    list.push(id);
    out.set(line.texture, list);
  }
  return out;
}

function gifTotalMs(asset: AssetRef | undefined): number | null {
  const meta = asset?.meta;
  if (meta === undefined) return null;
  if (meta.gifTotalMs !== undefined) return meta.gifTotalMs;
  if (meta.gifDelaysMs !== undefined) return meta.gifDelaysMs.reduce((s, d) => s + d, 0);
  return null;
}

const ph04: CheckRule = {
  id: 'PH-04',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['assets', 'lines', 'motion'],
  defaultEnabled: true,
  title: msg('rules.ph04Title'),
  check(ctx) {
    for (const [assetId, lines] of animatedGifLines(ctx)) {
      const asset = ctx.doc.assets.get(assetId);
      if (asset === undefined || asset.missing === true) continue;
      if (gifTotalMs(asset) !== 0) continue;
      emitProblem(ctx, {
        id: problemId('PH-04', 'asset', assetId),
        message: msg('rules.ph04What', { path: asset.path, count: lines.length }),
        why: msg('rules.ph04Why'),
        at: {
          asset: assetId,
          lines,
          ...(lines[0] !== undefined ? { line: lines[0] } : {}),
          field: FIELD.lineTexture,
        },
        fixes: [
          fixRef('problems.fix.reencodeGif', msg('rules.fixReencodeGif', { ms: 100 }), {
            asset: assetId,
            frameMs: 100,
          }),
          fixRef('problems.fix.removeGifProgress', msg('rules.fixRemoveGifProgress'), { lines }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-05 package name hazard
// ---------------------------------------------------------------------------------------------------------------

const ph05: CheckRule = {
  id: 'PH-05',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['assets'],
  defaultEnabled: true,
  title: msg('rules.ph05Title'),
  check(ctx) {
    const taken = assetPaths(ctx.doc);
    for (const asset of ctx.doc.assets.values()) {
      const skin = isSkinTriggerName(asset.path);
      const batch = isBatchExportMarker(asset.path);
      if (!skin && !batch) continue;
      const path = safePackagePath(asset.path, taken);
      taken.add(path);
      emitProblem(ctx, {
        id: problemId('PH-05', 'asset', asset.id),
        message: msg(batch ? 'rules.ph05Export' : 'rules.ph05Skin', { path: asset.path }),
        why: msg(batch ? 'rules.ph05ExportWhy' : 'rules.ph05SkinWhy'),
        at: { asset: asset.id, field: FIELD.assetPath },
        fixes: [fixRef('problems.fix.renameAsset', msg('rules.fixRenameTo', { path }), { asset: asset.id, path })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-06 Bezier speed segment in 1.7 speed mode
// ---------------------------------------------------------------------------------------------------------------

const ph06: CheckRule = {
  id: 'PH-06',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.ph06Title'),
  check(ctx, only) {
    if (ctx.doc.pkg.speedMode !== 'rpe170') return;
    for (const line of linesToCheck(ctx, only)) {
      const keys = new Map<KeyId, Key>();
      for (const track of propTracks(ctx, line, 'speed')) {
        const list = trackKeys(ctx, track.id);
        list.forEach((k, i) => {
          if (i < list.length - 1 && k.ease.kind === 'bezier') keys.set(k.id, k);
        });
      }
      // What is really written (an exporter bug would show here first).
      for (const layer of ctx.engine.irLine(line).eventLayers) {
        for (const ev of layer?.speedEvents ?? []) {
          if (ev.bezier === 0 || ev.srcKey === null) continue;
          const k = ctx.doc.keys.get(ev.srcKey);
          if (k !== undefined) keys.set(k.id, k);
        }
      }
      if (keys.size === 0) continue;
      const list = [...keys.values()].sort((a, b) => Beats.cmp(a.t, b.t));
      const first = list[0];
      if (first === undefined) continue;
      const ids = list.map((k) => k.id);
      emitProblem(ctx, {
        id: problemId('PH-06', 'line', line),
        message: msg('rules.ph06What', { count: list.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg('rules.ph06Why'),
        at: { line, beat: first.t, keys: ids, field: FIELD.segmentEase },
        fixes: [
          fixRef('problems.fix.setEase', msg('rules.fixMakeLinear'), { segments: ids, ease: easeArg(EaseApi.LINEAR) }),
          goTo(first.t, { line, keys: ids }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-07 extra.json effect variables Phira rejects
// ---------------------------------------------------------------------------------------------------------------

/** The variable shape Phira's untagged parser sees: JSON null is tried as a float first (P§5.1). */
function writtenVarType(type: EffectVarType, kind: 'null' | 'const' | 'track'): EffectVarType | 'null' {
  return kind === 'null' ? 'null' : type;
}

function varTypeMatches(written: EffectVarType | 'null', want: EffectVarType): boolean {
  return written === 'null' ? want === 'float' : written === want;
}

function emitVarMismatch(
  ctx: CheckContext,
  effect: Effect,
  name: string,
  given: EffectVarType | 'null',
  spec: UniformSpec,
): void {
  emitProblem(ctx, {
    id: problemId('PH-07', 'effect', `${String(effect.id)}-${name}`),
    message: msg('rules.ph07Mismatch', { name, given, want: spec.type }),
    why: msg('rules.ph07Why'),
    at: { effect: effect.id, beat: effect.start, field: FIELD.effectVars },
    fixes: [
      fixRef(
        'problems.fix.setEffectVar',
        msg('rules.fixSetVarDefault', {
          name,
          value: Array.isArray(spec.value) ? fmtPoints(spec.value) : fmtNum(spec.value as number),
        }),
        { effect: effect.id, name, type: spec.type, value: spec.value },
      ),
      fixRef('problems.fix.deleteEffect', msg('rules.fixDeleteEffect'), { effect: effect.id }),
    ],
  });
}

const ph07: CheckRule = {
  id: 'PH-07',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['effects', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph07Title'),
  check(ctx) {
    const sources = contextExtensions(ctx).shaderSources;
    for (const id of ctx.doc.effectOrder) {
      const effect = ctx.doc.effects.get(id);
      if (effect === undefined) continue;
      let uniforms: readonly UniformSpec[];
      if (effect.shader.kind === 'builtin') {
        uniforms = BUILTIN_SHADER_UNIFORMS[effect.shader.name];
      } else {
        const assetId = effect.shader.asset;
        const src = sources?.get(assetId);
        if (src === undefined) continue;
        const path = assetLabel(ctx.doc.assets.get(assetId), assetId);
        const specs: UniformSpec[] = [];
        for (const d of parseShaderDefaults(src)) {
          if (d.ok) {
            specs.push(d.spec);
            continue;
          }
          emitProblem(ctx, {
            id: problemId('PH-07', 'effect', `${String(effect.id)}-default-${d.name}`),
            message:
              d.reason === 'type'
                ? msg('rules.ph07DefaultType', { path, name: d.name, glslType: d.glslType })
                : msg('rules.ph07DefaultValue', { path, name: d.name }),
            why: msg('rules.ph07Why'),
            at: { effect: effect.id, asset: assetId, beat: effect.start, field: FIELD.effectShader },
            fixes: [
              navFix('assets.replace', msg('rules.fixReplaceShader'), { asset: assetId }),
              fixRef('problems.fix.deleteEffect', msg('rules.fixDeleteEffect'), { effect: effect.id }),
            ],
          });
        }
        uniforms = specs;
      }
      for (const v of effect.vars) {
        const spec = uniforms.find((u) => u.name === v.name);
        if (spec === undefined) continue;
        const given = writtenVarType(v.type, v.value.kind);
        if (!varTypeMatches(given, spec.type)) emitVarMismatch(ctx, effect, v.name, given, spec);
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-08 easing slice with f(L) = f(R)
// ---------------------------------------------------------------------------------------------------------------

/** Decimals of slice bounds in messages. */
const SLICE_DIGITS = 6;

function degenerate(desc: PhiraTweenDesc): boolean {
  if (desc.kind !== 'clamped') return false;
  const den = fr(EaseApi.staticY(desc.tween, desc.r) - EaseApi.staticY(desc.tween, desc.l));
  return den === 0 || !Number.isFinite(den);
}

function lineEventLists(ctx: CheckContext, line: LineId): ReadonlyArray<readonly IrEvent<unknown>[]> {
  const ir = ctx.engine.irLine(line);
  const lists: (readonly IrEvent<unknown>[])[] = [];
  const pkg = ctx.doc.pkg;
  // Only the Legacy sub-mode of the 170 integrator evaluates a speed easing as a sliced curve (its height follows
  // f, F§7.3), where f(L) = f(R) divides by zero. The Modern sub-mode (RPEVersion >= 170) integrates the slice with
  // IntClampedTween, which falls back to x^2/2 in that case; the classic integrator plays speed straight.
  const slicedSpeed = pkg.speedMode === 'rpe170' && pkg.rpeVersion < 170;
  for (const layer of ir.eventLayers) {
    if (layer === null) continue;
    for (const l of [layer.alphaEvents, layer.moveXEvents, layer.moveYEvents, layer.rotateEvents]) {
      if (l !== undefined) lists.push(l);
    }
    if (slicedSpeed && layer.speedEvents !== undefined) lists.push(layer.speedEvents);
  }
  const ext = ir.extended;
  if (ext !== 'absent' && ext !== null) {
    for (const l of [
      ext.scaleXEvents,
      ext.scaleYEvents,
      ext.colorEvents,
      ext.textEvents,
      ext.inclineEvents,
      ext.paintEvents,
      ext.gifEvents,
    ]) {
      if (l !== undefined) lists.push(l);
    }
  }
  return lists;
}

function extraKeyframeLists(ctx: CheckContext): (readonly IrExtraKeyframe<unknown>[])[] {
  const extra = ctx.engine.irExtra();
  const out: (readonly IrExtraKeyframe<unknown>[])[] = [];
  if (extra === null) return out;
  for (const e of extra.effects) {
    for (const [, v] of e.vars)
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object')
        out.push(v as readonly IrExtraKeyframe<unknown>[]);
  }
  for (const v of extra.videos) {
    for (const anim of [v.alpha, v.dim]) if (Array.isArray(anim)) out.push(anim as readonly IrExtraKeyframe<unknown>[]);
  }
  return out;
}

function ownerParams(ctx: CheckContext, track: Track): Record<string, string> {
  const o = track.owner;
  switch (o.kind) {
    case 'line':
      return { owner: 'line', prop: o.prop, line: label(ctx, o.line), name: '', which: '' };
    case 'effectVar':
      return { owner: 'effect', prop: '', line: '', name: o.name, which: '' };
    case 'videoVar':
      return { owner: 'video', prop: '', line: '', name: '', which: o.which };
  }
}

const ph08: CheckRule = {
  id: 'PH-08',
  category: 'phira',
  severity: 'error',
  level: 'guided',
  scope: 'chart',
  deps: ['motion', 'effects', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.ph08Title'),
  check(ctx) {
    const bad = new Map<KeyId, { readonly l: number; readonly r: number }>();
    for (const line of ctx.doc.lineOrder) {
      for (const list of lineEventLists(ctx, line)) {
        for (const ev of list) {
          if (ev.srcKey === null) continue;
          const desc = EaseApi.resolveRpe(ev.easingType, ev.easingLeft, ev.easingRight, ev.bezier, ev.bezierPoints);
          if (degenerate(desc) && desc.kind === 'clamped') bad.set(ev.srcKey, { l: desc.l, r: desc.r });
        }
      }
    }
    for (const list of extraKeyframeLists(ctx)) {
      for (const kf of list) {
        if (kf.srcKey === null) continue;
        const desc = resolveExtra(kf.easingType, kf.easingLeft, kf.easingRight);
        if (degenerate(desc) && desc.kind === 'clamped') bad.set(kf.srcKey, { l: desc.l, r: desc.r });
      }
    }
    for (const [keyId, slice] of bad) {
      const key = ctx.doc.keys.get(keyId);
      const track = key === undefined ? undefined : ctx.doc.tracks.get(key.track);
      if (key === undefined || track === undefined) continue;
      const line = trackLine(track);
      const whole: Ease = wholeEase(key.ease);
      emitProblem(ctx, {
        id: problemId('PH-08', 'key', keyId),
        message: msg('rules.ph08What', {
          ...ownerParams(ctx, track),
          ...easeParams(whole),
          beat: fmtBeat(key.t),
          // Slices that collapse are often a hair wide (0.9999 to 1): show enough digits to tell the ends apart.
          left: fmtNum(slice.l, SLICE_DIGITS),
          right: fmtNum(slice.r, SLICE_DIGITS),
        }),
        why: msg('rules.ph08Why'),
        at: {
          ...(line !== null ? { line } : {}),
          ...(track.owner.kind === 'effectVar' ? { effect: track.owner.effect } : {}),
          beat: key.t,
          keys: [keyId],
          tracks: [track.id],
          field: FIELD.segmentEase,
        },
        fixes: [
          fixRef('problems.fix.setEase', msg('rules.fixResetSlice'), { segments: [keyId], ease: easeArg(whole) }),
          goTo(key.t, { ...(line !== null ? { line } : {}), keys: [keyId] }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-09 track starts after beat 0 (extrapolated lead-in)
// ---------------------------------------------------------------------------------------------------------------

const ph09: CheckRule = {
  id: 'PH-09',
  category: 'phira',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.ph09Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      for (const [prop, ids] of ctx.idx.tracksOfLine(line)) {
        if (leadInRule(prop, ctx.doc.pkg.speedMode) !== 'extrapolate') continue;
        for (const trackId of ids) {
          const track = ctx.doc.tracks.get(trackId);
          const first = trackKeys(ctx, trackId)[0];
          if (track === undefined || first === undefined) continue;
          if (Beats.cmp(first.t, Beats.ZERO) <= 0) continue;
          const layer = layerOf(track);
          const v0 = ctx.engine.channelValue(line, prop, 0, layer);
          const why = Number.isNaN(v0)
            ? 'rules.ph09WhyNaN'
            : prop === 'alpha' && v0 < 0
              ? 'rules.ph09WhyAlpha'
              : 'rules.ph09Why';
          emitProblem(ctx, {
            id: problemId('PH-09', 'track', trackId),
            message: msg('rules.ph09What', {
              prop,
              line: label(ctx, line),
              layer,
              beat: fmtBeat(first.t),
              value: Number.isNaN(v0) ? 'NaN' : fmtNum(v0),
            }),
            why: msg(why),
            at: { line, beat: first.t, keys: [first.id], tracks: [trackId], field: FIELD.segmentEase },
            fixes: [
              fixRef('problems.fix.holdLeadIn', msg('rules.fixHoldFirstValue'), { track: trackId }),
              goTo(first.t, { line, keys: [first.id] }),
            ],
          });
        }
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-10 / PH-11 line without opacity
// ---------------------------------------------------------------------------------------------------------------

/** Properties whose keyframes make a plain line without notes look like a decoration meant to be seen. */
const DECOR_PROPS: readonly LineProp[] = ['moveX', 'moveY', 'rotate', 'scaleX', 'scaleY', 'color'];

/** Lines a video is attached to (the video follows the line and ignores its opacity, R§8). */
function videoAnchorLines(ctx: CheckContext): ReadonlySet<LineId> {
  const out = new Set<LineId>();
  for (const v of ctx.doc.videos.values()) if (v.attachLine !== null) out.add(v.attachLine);
  return out;
}

/**
 * Why a line without opacity matters, or null when it is invisible on purpose. A plain line without notes is a rig
 * when it moves other lines (a parent) or carries a video, and nothing to look at when it has no motion either (ST-04
 * reports lines without any keyframes); one that moves on its own looks like a decoration that was meant to be seen.
 */
function ph10Why(ctx: CheckContext, line: LineId, videoAnchors: ReadonlySet<LineId>): MessageRef | null {
  const kind = kindOf(ctx, line);
  if (kind !== 'normal') return msg('rules.ph10WhyContent', { kind });
  if (ctx.idx.notesByLine(line).length > 0) return msg('rules.ph10WhyNotes');
  if (ctx.idx.childrenOf(line).length > 0 || videoAnchors.has(line)) return null;
  return DECOR_PROPS.some((prop) => hasProp(ctx, line, prop)) ? msg('rules.ph10WhyDecor') : null;
}

const ph10: CheckRule = {
  id: 'PH-10',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines', 'notes', 'effects'],
  defaultEnabled: true,
  title: msg('rules.ph10Title'),
  check(ctx, only) {
    let videoAnchors: ReadonlySet<LineId> | null = null;
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined || rec.attachUI !== null || hasProp(ctx, line, 'alpha')) continue;
      videoAnchors ??= videoAnchorLines(ctx);
      const why = ph10Why(ctx, line, videoAnchors);
      if (why === null) continue;
      emitProblem(ctx, {
        id: problemId('PH-10', 'line', line),
        message: msg('rules.ph10What', { line: label(ctx, line) }),
        why,
        at: { line, beat: Beats.ZERO, field: FIELD.lineOpacity },
        fixes: [fixRef('problems.fix.setLineOpacityFull', msg('rules.fixSetOpacityFull'), { line })],
      });
    }
  },
};

const ph11: CheckRule = {
  id: 'PH-11',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph11Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec?.attachUI == null || hasProp(ctx, line, 'alpha')) continue;
      emitProblem(ctx, {
        id: problemId('PH-11', 'line', line),
        message: msg('rules.ph11What', { line: label(ctx, line), element: rec.attachUI }),
        why: msg('rules.ph11Why'),
        at: { line, beat: Beats.ZERO, field: FIELD.lineOpacity },
        fixes: [fixRef('problems.fix.setLineOpacityFull', msg('rules.fixSetOpacityFull'), { line })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-12 notes on a UI-mover line
// ---------------------------------------------------------------------------------------------------------------

const ph12: CheckRule = {
  id: 'PH-12',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['lines', 'notes'],
  defaultEnabled: true,
  title: msg('rules.ph12Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec?.attachUI == null) continue;
      const notes = lineNotes(ctx, line);
      const first = notes[0];
      if (first === undefined) continue;
      const ids = notes.map((n) => n.id);
      emitProblem(ctx, {
        id: problemId('PH-12', 'line', line),
        message: msg('rules.ph12What', { count: notes.length, line: label(ctx, line) }),
        why: msg('rules.ph12Why'),
        at: { line, beat: first.t, notes: ids, field: FIELD.lineAttachUI },
        fixes: [
          fixRef('problems.fix.moveNotesToNewLine', msg('rules.fixMoveNotesToNewLine', { count: notes.length }), {
            line,
            notes: ids,
          }),
          selectNotes(notes),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-13 negative note speed on real Tap/Drag/Flick
// ---------------------------------------------------------------------------------------------------------------

const ph13: CheckRule = {
  id: 'PH-13',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['notes'],
  defaultEnabled: true,
  title: msg('rules.ph13Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      // Notes of a UI mover are never drawn whatever their speed (PH-12 reports them).
      if (isUiMoverLine(ctx, line)) continue;
      const notes = lineNotes(ctx, line).filter((n) => !n.fake && n.kind !== 'hold' && n.speed < 0);
      const first = notes[0];
      if (first === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-13', 'line', line),
        message: msg('rules.ph13What', { count: notes.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg(ctx.player.aggressiveCull ? 'rules.ph13Why' : 'rules.ph13WhyCullOff'),
        at: { line, beat: first.t, notes: notes.map((n) => n.id), field: FIELD.noteSpeed },
        fixes: [selectNotes(notes)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-14 note speed 0 on a line with distance effects
// ---------------------------------------------------------------------------------------------------------------

function activeControls(line: Line): ('pos' | 'alpha' | 'size' | 'y')[] {
  const out: ('pos' | 'alpha' | 'size' | 'y')[] = [];
  for (const which of ['pos', 'alpha', 'size', 'y'] as const) {
    if (!isInertCurve(line.controls[which])) out.push(which);
  }
  return out;
}

const ph14: CheckRule = {
  id: 'PH-14',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['notes', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph14Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined || rec.attachUI !== null || activeControls(rec).length === 0) continue;
      const notes = lineNotes(ctx, line).filter((n) => n.speed === 0);
      const first = notes[0];
      if (first === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-14', 'line', line),
        message: msg('rules.ph14What', { count: notes.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg('rules.ph14Why'),
        at: { line, beat: first.t, notes: notes.map((n) => n.id), field: FIELD.noteSpeed },
        fixes: [selectNotes(notes)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-17 distance-effect curve inert in Phira but active in RPE
// ---------------------------------------------------------------------------------------------------------------

const ph17: CheckRule = {
  id: 'PH-17',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['lines'],
  defaultEnabled: true,
  title: msg('rules.ph17Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined) continue;
      for (const which of ['pos', 'alpha', 'size', 'y'] as const) {
        const curve = rec.controls[which];
        if (curve?.points.length !== 2 || !isInertCurve(curve) || isNeutralCurve(curve)) continue;
        const last = curve.points[1];
        if (last === undefined) continue;
        const fixed = { points: [...curve.points, { x: last.x + 1, easing: 1, value: last.value }] };
        emitProblem(ctx, {
          id: problemId('PH-17', 'control', `${String(line)}-${which}`),
          message: msg('rules.ph17What', { which, line: label(ctx, line) }),
          why: msg('rules.ph17Why'),
          at: { line, field: controlField(which) },
          fixes: [
            fixRef('problems.fix.setControl', msg('rules.fixAddThirdPoint'), {
              line,
              which,
              curve: controlCurveArg(fixed),
            }),
          ],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-18 animated note tilt
// ---------------------------------------------------------------------------------------------------------------

const TILT_EPS_DEG = 1e-4;

const ph18: CheckRule = {
  id: 'PH-18',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['motion'],
  defaultEnabled: true,
  title: msg('rules.ph18Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const track = propTracks(ctx, line, 'incline')[0];
      if (track === undefined) continue;
      const keys = trackKeys(ctx, track.id);
      const v0 = ctx.engine.channelValue(line, 'incline', 0);
      if (!Number.isFinite(v0)) continue;
      // Phira keeps f32 values: compare in f32, with a tolerance far below a visible tilt difference.
      const differs = (v: number): boolean => Math.abs(fr(v) - v0) > TILT_EPS_DEG;
      const changes = keys.filter((k) => differs(k.vIn as number) || differs(k.vOut as number));
      const first = changes[0];
      if (first === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-18', 'line', line),
        message: msg('rules.ph18What', { line: label(ctx, line), from: fmtNum(v0), beat: fmtBeat(first.t) }),
        why: msg('rules.ph18Why'),
        at: { line, beat: first.t, keys: changes.map((k) => k.id), tracks: [track.id], field: FIELD.lineIncline },
        fixes: [
          fixRef('problems.fix.setIncline', msg('rules.fixKeepTilt', { deg: fmtNum(v0) }), { line, degrees: v0 }),
          goTo(first.t, { line, keys: [first.id] }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-19 eased speed segments Phira plays straight (legacy speed mode)
// ---------------------------------------------------------------------------------------------------------------

const ph19: CheckRule = {
  id: 'PH-19',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.ph19Title'),
  check(ctx, only) {
    if (ctx.doc.pkg.speedMode !== 'legacy') return;
    for (const line of linesToCheck(ctx, only)) {
      const keys: Key[] = [];
      for (const track of propTracks(ctx, line, 'speed')) {
        const list = trackKeys(ctx, track.id);
        list.forEach((k, i) => {
          const ig = k.src?.ignoredEase;
          if (i < list.length - 1 && ig !== undefined && (ig.easingType > 1 || ig.bezier !== 0)) keys.push(k);
        });
      }
      keys.sort((a, b) => Beats.cmp(a.t, b.t));
      const first = keys[0];
      if (first === undefined) continue;
      const ids = keys.map((k) => k.id);
      emitProblem(ctx, {
        id: problemId('PH-19', 'line', line),
        message: msg('rules.ph19What', { count: keys.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg('rules.ph19Why'),
        at: { line, beat: first.t, keys: ids, field: FIELD.pkgSpeedMode },
        fixes: [
          navFix('project.settings', msg('rules.fixOpenSpeedSettings'), { section: 'speed' }),
          goTo(first.t, { line, keys: ids }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-20 Bezier curves sharing Phira's 0.01-rounded cache key
// ---------------------------------------------------------------------------------------------------------------

const ph20: CheckRule = {
  id: 'PH-20',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'chart',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph20Title'),
  check(ctx) {
    const uses = scanBezierUses(ctx.engine.ir().judgeLineList);
    const winners = new Map<string, BezierUse>();
    const losers = new Map<string, { readonly winner: BezierUse; readonly uses: BezierUse[] }>();
    for (const u of uses) {
      const w = winners.get(u.key);
      if (w === undefined) {
        winners.set(u.key, u);
        continue;
      }
      if (samePoints(w.points, u.points)) continue;
      const group = `${u.key}/${u.points.join(',')}`;
      const g = losers.get(group) ?? { winner: w, uses: [] };
      g.uses.push(u);
      losers.set(group, g);
    }
    const taken = new Set(winners.keys());
    for (const [group, { winner, uses: list }] of losers) {
      const first = list[0];
      if (first === undefined) continue;
      const segs = [...new Set(list.map((u) => u.srcKey).filter((k): k is KeyId => k !== null))];
      const firstKey = segs[0] === undefined ? undefined : ctx.doc.keys.get(segs[0]);
      const fixes: FixRef[] = [];
      if (segs.length > 0) {
        fixes.push(
          fixRef('problems.fix.setEase', msg('rules.fixMakeIdentical', { points: fmtPoints(winner.points) }), {
            segments: segs,
            ease: easeArg({ kind: 'bezier', p: winner.points }),
          }),
        );
        const nudged = nudgeBezier(first.points, taken);
        if (nudged !== null) {
          taken.add(EaseApi.bezierKey(nudged));
          fixes.push(
            fixRef('problems.fix.setEase', msg('rules.fixNudgeCurve', { points: fmtPoints(nudged) }), {
              segments: segs,
              ease: easeArg({ kind: 'bezier', p: nudged }),
            }),
          );
        }
      }
      if (firstKey !== undefined && first.line !== null) fixes.push(goTo(firstKey.t, { line: first.line, keys: segs }));
      emitProblem(ctx, {
        id: problemId('PH-20', 'curve', group),
        message: msg('rules.ph20What', {
          count: list.length,
          points: fmtPoints(first.points),
          winner: fmtPoints(winner.points),
          line: winner.line === null ? `#${String(winner.lineIndex)}` : label(ctx, winner.line),
        }),
        why: msg('rules.ph20Why'),
        at: {
          ...(first.line !== null ? { line: first.line } : {}),
          ...(firstKey !== undefined ? { beat: firstKey.t } : {}),
          ...(segs.length > 0 ? { keys: segs } : {}),
          field: FIELD.segmentEase,
        },
        fixes,
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-21 InOutBack / InOutElastic differ in RPE
// ---------------------------------------------------------------------------------------------------------------

const ph21: CheckRule = {
  id: 'PH-21',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion'],
  defaultEnabled: true,
  title: msg('rules.ph21Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const keys: Key[] = [];
      for (const ids of ctx.idx.tracksOfLine(line).values()) {
        for (const trackId of ids) {
          const list = trackKeys(ctx, trackId);
          list.forEach((k, i) => {
            if (i < list.length - 1 && k.ease.kind === 'tween' && EaseApi.catalog(k.ease.id)?.differsInRpe === true) {
              keys.push(k);
            }
          });
        }
      }
      keys.sort((a, b) => Beats.cmp(a.t, b.t));
      const first = keys[0];
      if (first === undefined) continue;
      const ids = keys.map((k) => k.id);
      emitProblem(ctx, {
        id: problemId('PH-21', 'line', line),
        message: msg('rules.ph21What', { count: keys.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg('rules.ph21Why'),
        at: { line, beat: first.t, keys: ids, field: FIELD.segmentEase },
        fixes: [goTo(first.t, { line, keys: ids })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-22 RPE line fields Phira ignores
// ---------------------------------------------------------------------------------------------------------------

/**
 * Whether an RPE `skewControl` is RPE's default (absent, null, or control points whose `skew` is 0 or missing). The
 * import report's test (formats/rpe/kept.ts `skewIsDefault`, CR-W3-02b-5), so the checker and the importer agree:
 * RPE writes `[{x: 0, skew: 0}, {x: 9999999, skew: 0}]` on every line and only a real skew is reported (UX-090).
 */
export function rpeSkewIsDefault(skew: Json | null | undefined): boolean {
  return skewIsDefault(skew ?? undefined);
}

const ph22: CheckRule = {
  id: 'PH-22',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'line',
  deps: ['lines'],
  defaultEnabled: true,
  title: msg('rules.ph22Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined) continue;
      const fields: ('bpmfactor' | 'anchor' | 'skewControl')[] = [];
      // Which fields are set, as the select key of the message ('b', 'a', 's' in that order: "bs", "bas", ...).
      let which = '';
      if (rec.rpe.bpmfactor !== 1) {
        fields.push('bpmfactor');
        which += 'b';
      }
      // RPE uses the anchor only to place a texture (formats/rpe/kept.ts flags it on textured lines only).
      if (rec.texture !== null && (rec.rpe.anchor[0] !== 0.5 || rec.rpe.anchor[1] !== 0.5)) {
        fields.push('anchor');
        which += 'a';
      }
      // RPE writes a neutral skewControl on every line: only a skew other than 0 makes RPE draw differently.
      if (!rpeSkewIsDefault(rec.rpe.skewControl)) {
        fields.push('skewControl');
        which += 's';
      }
      const firstField = fields[0];
      if (firstField === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-22', 'line', line),
        message: msg('rules.ph22What', {
          line: label(ctx, line),
          fields: which,
          bpmfactor: fmtNum(rec.rpe.bpmfactor),
          anchor: fmtPoints(rec.rpe.anchor),
        }),
        why: msg('rules.ph22Why'),
        at: {
          line,
          field:
            firstField === 'bpmfactor'
              ? FIELD.lineBpmfactor
              : firstField === 'anchor'
                ? FIELD.lineAnchor
                : FIELD.lineSkewControl,
        },
        fixes: [fixRef('problems.fix.resetRpeLineFields', msg('rules.fixResetRpeFields'), { line, fields })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-23 opacity below 0 while the line has notes
// ---------------------------------------------------------------------------------------------------------------

const ph23: CheckRule = {
  id: 'PH-23',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'notes', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.ph23Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined || rec.attachUI !== null || !hasProp(ctx, line, 'alpha')) continue;
      const notes = lineNotes(ctx, line)
        .filter((n) => !n.fake)
        .map((n) => ({ n, sec: ctx.engine.noteSec(n.id) }))
        .sort((a, b) => a.sec - b.sec || a.n.id - b.n.id);
      let span: Note[] = [];
      const flush = (): void => {
        const first = span[0];
        if (first !== undefined) {
          emitProblem(ctx, {
            id: problemId('PH-23', 'span', `${String(line)}-${String(first.id)}`),
            message: msg('rules.ph23What', { count: span.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
            why: msg('rules.ph23Why'),
            at: { line, beat: first.t, notes: span.map((n) => n.id), field: FIELD.lineOpacity },
            fixes: [revealFix(msg('rules.fixGoToSpan'), first.t, { line, notes: span.map((n) => n.id) })],
          });
        }
        span = [];
      };
      for (const { n, sec } of notes) {
        if (ctx.engine.lineState(line, sec).alpha < 0) span.push(n);
        else flush();
      }
      flush();
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-24 text/paint data on a textured line, GIF data on a line.png line
// ---------------------------------------------------------------------------------------------------------------

const IGNORABLE: readonly { readonly prop: 'text' | 'paint' | 'gif'; readonly key: string }[] = [
  { prop: 'text', key: 'textEvents' },
  { prop: 'paint', key: 'paintEvents' },
  { prop: 'gif', key: 'gifEvents' },
];

function ph24Why(prop: 'text' | 'paint' | 'gif', textured: boolean): string {
  if (prop === 'gif') return 'rules.ph24WhyGifPlain';
  if (prop === 'paint') return 'rules.ph24WhyPaintImage';
  return textured ? 'rules.ph24WhyTextImage' : 'rules.ph24WhyTextPaint';
}

const ph24: CheckRule = {
  id: 'PH-24',
  category: 'phira',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph24Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec === undefined) continue;
      const kind = kindOf(ctx, line);
      const textured = rec.texture !== null;
      for (const { prop, key } of IGNORABLE) {
        const track = hasProp(ctx, line, prop) && !propAppliesTo(prop, kind);
        // GIF data on a texture that is not a GIF is the fatal PH-35, not ignored data.
        if (track && prop === 'gif' && textured) continue;
        const raw = rec.rpe.ignored.some((d) => d.writeBack === 'extended' && d.key === key);
        if (!track && !raw) continue;
        emitProblem(ctx, {
          id: problemId('PH-24', 'line', `${String(line)}-${prop}`),
          message: msg('rules.ph24What', { item: prop, line: label(ctx, line), kind }),
          why: msg(ph24Why(prop, textured)),
          at: { line, field: textured || prop === 'gif' ? FIELD.lineTexture : FIELD.lineText },
          fixes: [
            fixRef('problems.fix.removeIgnoredLineData', msg('rules.fixRemoveIgnoredData'), {
              line,
              props: track ? [prop] : [],
              keys: raw ? [key] : [],
            }),
            navFix('inspector.focusField', msg('rules.fixChangeLineType'), { field: FIELD.lineTexture }),
          ],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-25 more than 4 layers
// ---------------------------------------------------------------------------------------------------------------

const ph25: CheckRule = {
  id: 'PH-25',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion'],
  defaultEnabled: true,
  title: msg('rules.ph25Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      for (const prop of LAYERED_PROPS) {
        const tracks = propTracks(ctx, line, prop);
        const top = Math.max(-1, ...tracks.map(layerOf));
        if (top < RPE_VISIBLE_LAYERS) continue;
        emitProblem(ctx, {
          id: problemId('PH-25', 'line', `${String(line)}-${prop}`),
          message: msg('rules.ph25What', { prop, line: label(ctx, line), count: top + 1 }),
          why: msg('rules.ph25Why'),
          at: { line, tracks: tracks.map((t) => t.id), field: FIELD.lineLayers },
          fixes: [navFix('tools.flatten', msg('rules.fixFlatten'), { line, prop })],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-26 several lines move the same UI element
// ---------------------------------------------------------------------------------------------------------------

const ph26: CheckRule = {
  id: 'PH-26',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'chart',
  deps: ['lines'],
  defaultEnabled: true,
  title: msg('rules.ph26Title'),
  check(ctx) {
    const byElement = new Map<UiElement, LineId[]>();
    for (const id of ctx.doc.lineOrder) {
      const el = ctx.doc.lines.get(id)?.attachUI;
      if (el == null) continue;
      byElement.set(el, [...(byElement.get(el) ?? []), id]);
    }
    for (const [element, lines] of byElement) {
      const winner = lines[lines.length - 1];
      if (lines.length < 2 || winner === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-26', 'ui', element),
        message: msg('rules.ph26What', { count: lines.length, element }),
        why: msg('rules.ph26Why', { winner: label(ctx, winner) }),
        at: { line: winner, lines, field: FIELD.lineAttachUI },
        fixes: lines
          .slice(0, -1)
          .map((l) => revealFix(msg('rules.fixGoToLine', { line: label(ctx, l) }), Beats.ZERO, { line: l })),
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-27 image wider or taller than 2048 px
// ---------------------------------------------------------------------------------------------------------------

const ph27: CheckRule = {
  id: 'PH-27',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['assets', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph27Title'),
  check(ctx) {
    const users = new Map<AssetId, LineId[]>();
    for (const id of ctx.doc.lineOrder) {
      const tex = ctx.doc.lines.get(id)?.texture;
      if (tex != null) users.set(tex, [...(users.get(tex) ?? []), id]);
    }
    for (const [assetId, lines] of users) {
      const asset = ctx.doc.assets.get(assetId);
      const w = asset?.meta?.width ?? 0;
      const h = asset?.meta?.height ?? 0;
      if (asset === undefined || asset.missing === true || Math.max(w, h) <= MAX_TEXTURE_SIDE_PX) continue;
      emitProblem(ctx, {
        id: problemId('PH-27', 'asset', assetId),
        message: msg('rules.ph27What', { path: asset.path, w, h }),
        why: msg('rules.ph27Why'),
        at: { asset: assetId, lines, ...(lines[0] !== undefined ? { line: lines[0] } : {}), field: FIELD.lineTexture },
        fixes: [
          fixRef('problems.fix.downscaleImage', msg('rules.fixDownscale', { px: MAX_TEXTURE_SIDE_PX }), {
            asset: assetId,
            maxSide: MAX_TEXTURE_SIDE_PX,
          }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-28 paint line brush depends on the screen
// ---------------------------------------------------------------------------------------------------------------

const ph28: CheckRule = {
  id: 'PH-28',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph28Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      if (kindOf(ctx, line) !== 'paint') continue;
      emitProblem(ctx, {
        id: problemId('PH-28', 'line', line),
        message: msg('rules.ph28What', { line: label(ctx, line) }),
        why: msg('rules.ph28Why'),
        at: { line },
        fixes: [goToLine(ctx, line)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-29 attached video on a line without colour
// ---------------------------------------------------------------------------------------------------------------

const ph29: CheckRule = {
  id: 'PH-29',
  category: 'phira',
  severity: 'warning',
  level: 'full',
  scope: 'chart',
  deps: ['effects', 'motion', 'lines'],
  defaultEnabled: true,
  title: msg('rules.ph29Title'),
  check(ctx) {
    for (const video of ctx.doc.videos.values()) {
      const line = video.attachLine;
      if (line === null || !ctx.doc.lines.has(line) || hasProp(ctx, line, 'color')) continue;
      emitProblem(ctx, {
        id: problemId('PH-29', 'video', video.id),
        message: msg('rules.ph29What', {
          path: assetLabel(ctx.doc.assets.get(video.asset), video.asset),
          line: label(ctx, line),
        }),
        why: msg('rules.ph29Why'),
        at: { line, beat: video.time, asset: video.asset, field: FIELD.lineColor },
        fixes: [fixRef('problems.fix.addWhiteColorKey', msg('rules.fixAddWhiteColour'), { line })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-30 effect variable keys start after the effect starts
// ---------------------------------------------------------------------------------------------------------------

const ph30: CheckRule = {
  id: 'PH-30',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'chart',
  deps: ['effects', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.ph30Title'),
  check(ctx) {
    for (const id of ctx.doc.effectOrder) {
      const effect = ctx.doc.effects.get(id);
      if (effect === undefined) continue;
      for (const v of effect.vars) {
        if (v.value.kind !== 'track') continue;
        const first = trackKeys(ctx, v.value.track)[0];
        if (first === undefined || Beats.cmp(first.t, effect.start) <= 0) continue;
        emitProblem(ctx, {
          id: problemId('PH-30', 'effect', `${String(effect.id)}-${v.name}`),
          message: msg('rules.ph30What', { name: v.name, beat: fmtBeat(first.t), start: fmtBeat(effect.start) }),
          why: msg('rules.ph30Why'),
          at: {
            effect: effect.id,
            beat: effect.start,
            keys: [first.id],
            tracks: [v.value.track],
            field: FIELD.effectVars,
          },
          fixes: [
            fixRef('problems.fix.holdVarLeadIn', msg('rules.fixHoldVarFromStart', { beat: fmtBeat(effect.start) }), {
              track: v.value.track,
              beat: { w: effect.start.w, n: effect.start.n, d: effect.start.d },
            }),
          ],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-31 non-ASCII asset name
// ---------------------------------------------------------------------------------------------------------------

const ph31: CheckRule = {
  id: 'PH-31',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['assets'],
  defaultEnabled: true,
  title: msg('rules.ph31Title'),
  check(ctx) {
    const taken = assetPaths(ctx.doc);
    for (const asset of ctx.doc.assets.values()) {
      if (!hasNonAscii(asset.path)) continue;
      const path = asciiPath(asset.path, taken);
      taken.add(path);
      emitProblem(ctx, {
        id: problemId('PH-31', 'asset', asset.id),
        message: msg('rules.ph31What', { path: asset.path }),
        why: msg('rules.ph31Why'),
        at: { asset: asset.id, field: FIELD.assetPath },
        fixes: [fixRef('problems.fix.renameAsset', msg('rules.fixRenameTo', { path }), { asset: asset.id, path })],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-32 hold whose tail is nearer than its head
// ---------------------------------------------------------------------------------------------------------------

const ph32: CheckRule = {
  id: 'PH-32',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['notes', 'motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.ph32Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      if (isUiMoverLine(ctx, line)) continue;
      const holds = lineNotes(ctx, line).filter((n) => {
        if (n.kind !== 'hold' || Beats.eq(n.t, n.end)) return false;
        const h0 = ctx.engine.floorPosition(line, ctx.engine.noteSec(n.id));
        const h1 = ctx.engine.floorPosition(line, ctx.engine.noteEndSec(n.id));
        return (h1 - h0) * n.speed < 0;
      });
      const first = holds[0];
      if (first === undefined) continue;
      emitProblem(ctx, {
        id: problemId('PH-32', 'line', line),
        message: msg('rules.ph32What', { count: holds.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: msg('rules.ph32Why'),
        at: { line, beat: first.t, notes: holds.map((n) => n.id), field: FIELD.noteEnd },
        fixes: [selectNotes(holds)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-33 custom shader fails to compile in the preview
// ---------------------------------------------------------------------------------------------------------------

const ph33: CheckRule = {
  id: 'PH-33',
  category: 'phira',
  severity: 'error',
  level: 'guided',
  scope: 'chart',
  deps: ['effects', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph33Title'),
  check(ctx) {
    const errors = contextExtensions(ctx).shaderErrors;
    if (errors === undefined || errors.size === 0) return;
    for (const id of ctx.doc.effectOrder) {
      const effect = ctx.doc.effects.get(id);
      if (effect?.shader.kind !== 'asset') continue;
      const log = errors.get(effect.shader.asset);
      if (log === undefined) continue;
      const assetId = effect.shader.asset;
      const firstLine =
        log
          .split('\n')
          .find((l) => l.trim() !== '')
          ?.trim() ?? '';
      emitProblem(ctx, {
        id: problemId('PH-33', 'effect', effect.id),
        message: msg('rules.ph33What', {
          path: assetLabel(ctx.doc.assets.get(assetId), assetId),
          error: firstLine.length > 160 ? `${firstLine.slice(0, 159)}…` : firstLine,
        }),
        why: msg('rules.ph33Why'),
        at: { effect: effect.id, asset: assetId, beat: effect.start, field: FIELD.effectShader },
        fixes: [
          navFix('assets.replace', msg('rules.fixReplaceShader'), { asset: assetId }),
          fixRef('problems.fix.deleteEffect', msg('rules.fixDeleteEffect'), { effect: effect.id }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-34 RPEVersion was a float or a string
// ---------------------------------------------------------------------------------------------------------------

const ph34: CheckRule = {
  id: 'PH-34',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'chart',
  deps: ['pkg'],
  defaultEnabled: true,
  title: msg('rules.ph34Title'),
  check(ctx) {
    const raw = ctx.doc.pkg.rpeVersionRaw;
    if (raw === null) return;
    const version = ctx.doc.pkg.rpeVersion;
    emitProblem(ctx, {
      id: problemId('PH-34', 'chart', 'rpeVersion'),
      message: msg('rules.ph34What', { lexeme: raw.lexeme, kind: raw.kind, effective: version }),
      why: msg('rules.ph34Why'),
      at: { field: FIELD.pkgRpeVersion },
      fixes: [fixRef('problems.fix.writeIntegerRpeVersion', msg('rules.fixWriteInteger', { version }), { version })],
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-35 GIF progress on a texture that is not a GIF
// ---------------------------------------------------------------------------------------------------------------

const ph35: CheckRule = {
  id: 'PH-35',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'lines', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph35Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const rec = lineOf(ctx, line);
      if (rec?.texture == null) continue;
      const keys = hasProp(ctx, line, 'gif');
      if (!keys && !rec.gifAutoLoop) continue;
      const asset = ctx.doc.assets.get(rec.texture);
      if (isGifAsset(asset)) continue;
      emitProblem(ctx, {
        id: problemId('PH-35', 'line', line),
        message: msg('rules.ph35What', {
          line: label(ctx, line),
          auto: keys ? 'no' : 'yes',
          path: assetLabel(asset, rec.texture),
        }),
        why: msg('rules.ph35Why'),
        at: { line, asset: rec.texture, field: keys ? FIELD.lineTexture : FIELD.lineGifAutoLoop },
        fixes: [
          fixRef('problems.fix.removeGifProgress', msg('rules.fixRemoveGifProgress'), { lines: [line] }),
          navFix('inspector.focusField', msg('rules.fixChooseGif'), { field: FIELD.lineTexture }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-36 song-select preview range vs the music
// ---------------------------------------------------------------------------------------------------------------

const ph36: CheckRule = {
  id: 'PH-36',
  category: 'phira',
  severity: 'fatal',
  level: 'guided',
  scope: 'chart',
  deps: ['meta', 'assets'],
  defaultEnabled: true,
  title: msg('rules.ph36Title'),
  check(ctx) {
    const { previewStart: start, previewEnd: end } = ctx.doc.meta;
    const len = musicLengthSec(ctx);
    let message: ReturnType<typeof msg> | null = null;
    if (!(start >= 0)) {
      message = msg('rules.ph36Negative', { start: fmtSec(start) });
    } else if (end !== null && !(start < end)) {
      message = msg('rules.ph36Order', { start: fmtSec(start), end: fmtSec(end) });
    } else if (len !== null) {
      const checked = end ?? start + PREVIEW_NULL_END_SEC;
      if (checked > len - PREVIEW_END_MARGIN_SEC) {
        message =
          end === null
            ? msg('rules.ph36BeyondNull', { start: fmtSec(start), end: fmtSec(checked), length: fmtSec(len) })
            : msg('rules.ph36Beyond', { end: fmtSec(end), length: fmtSec(len) });
      }
    }
    if (message === null) return;
    const range = clampedPreviewRange(start, end, len);
    const fixes: FixRef[] = [];
    if (range !== null) {
      fixes.push(
        fixRef(
          'problems.fix.clampPreviewRange',
          range.end === null
            ? msg('rules.fixClampPreviewOpen', { start: fmtSec(range.start) })
            : msg('rules.fixClampPreview', { start: fmtSec(range.start), end: fmtSec(range.end) }),
          { start: range.start, end: range.end },
        ),
      );
    }
    fixes.push(navFix('inspector.focusField', msg('rules.fixEditPreview'), { field: FIELD.metaPreview }));
    emitProblem(ctx, {
      id: problemId('PH-36', 'chart', 'preview'),
      message,
      why: msg(range === null && len !== null ? 'rules.ph36WhyShort' : 'rules.ph36Why'),
      at: { field: FIELD.metaPreview, ...(ctx.doc.meta.music !== null ? { asset: ctx.doc.meta.music } : {}) },
      fixes,
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-37 zero-length hold
// ---------------------------------------------------------------------------------------------------------------

const ph37: CheckRule = {
  id: 'PH-37',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'line',
  deps: ['notes'],
  defaultEnabled: true,
  title: msg('rules.ph37Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      for (const n of lineNotes(ctx, line)) {
        if (n.kind !== 'hold' || !Beats.eq(n.t, n.end)) continue;
        emitProblem(ctx, {
          id: problemId('PH-37', 'note', n.id),
          message: msg('rules.ph37What', { line: label(ctx, line), beat: fmtBeat(n.t) }),
          why: msg('rules.ph37Why'),
          at: { line, beat: n.t, x: n.x, notes: [n.id], field: FIELD.noteEnd },
          fixes: [
            fixRef('problems.fix.extendHolds', msg('rules.fixExtendHold'), { notes: [n.id] }),
            fixRef('problems.fix.convertNotes', msg('rules.fixConvertToTap'), { notes: [n.id], kind: 'tap' }),
            goTo(n.t, { line, notes: [n.id] }),
          ],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-38 package hitsound file named tap.mp3 / flick.mp3 / drag.mp3
// ---------------------------------------------------------------------------------------------------------------

const ph38: CheckRule = {
  id: 'PH-38',
  category: 'phira',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['assets', 'notes'],
  defaultEnabled: true,
  title: msg('rules.ph38Title'),
  check(ctx) {
    const taken = assetPaths(ctx.doc);
    for (const asset of ctx.doc.assets.values()) {
      if (!RESERVED_HITSOUND_NAMES.includes(asset.path)) continue;
      const sound = asset.path.slice(0, -'.mp3'.length);
      // Notes that name the file: written as the reserved string, so Phira plays its built-in sound for them.
      const builtin: NoteId[] = [];
      const users: NoteId[] = [];
      for (const n of ctx.doc.notes.values()) {
        if (n.hitsound?.kind === 'builtin' && n.hitsound.sound === sound) builtin.push(n.id);
        else if (n.hitsound?.kind === 'asset' && n.hitsound.asset === asset.id) users.push(n.id);
      }
      const path = uniquePath(`${sound}-custom.mp3`, taken);
      taken.add(path);
      const notes = [...users, ...builtin];
      emitProblem(ctx, {
        id: problemId('PH-38', 'asset', asset.id),
        message: msg('rules.ph38What', { path: asset.path, count: notes.length }),
        why: msg('rules.ph38Why', { path: asset.path }),
        at: { asset: asset.id, ...(notes.length > 0 ? { notes } : {}), field: FIELD.assetPath },
        fixes: [
          fixRef('problems.fix.renameHitsoundAsset', msg('rules.fixRenameHitsound', { path, count: builtin.length }), {
            asset: asset.id,
            path,
            notes: builtin,
          }),
        ],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// PH-39 Bezier handle x outside [0, 1]
// ---------------------------------------------------------------------------------------------------------------

const ph39: CheckRule = {
  id: 'PH-39',
  category: 'phira',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion'],
  defaultEnabled: true,
  title: msg('rules.ph39Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      for (const [prop, ids] of ctx.idx.tracksOfLine(line)) {
        for (const trackId of ids) {
          const list = trackKeys(ctx, trackId);
          list.forEach((k, i) => {
            if (i === list.length - 1 || k.ease.kind !== 'bezier') return;
            const [x1, , x2] = k.ease.p;
            if (x1 >= 0 && x1 <= 1 && x2 >= 0 && x2 <= 1) return;
            emitProblem(ctx, {
              id: problemId('PH-39', 'key', k.id),
              message: msg('rules.ph39What', {
                points: fmtPoints(k.ease.p),
                prop,
                line: label(ctx, line),
                beat: fmtBeat(k.t),
              }),
              why: msg('rules.ph39Why'),
              at: { line, beat: k.t, keys: [k.id], tracks: [trackId], field: FIELD.segmentEase },
              fixes: [
                fixRef('problems.fix.setEase', msg('rules.fixClampHandles'), {
                  segments: [k.id],
                  ease: easeArg(clampBezierHandles(k.ease)),
                }),
                goTo(k.t, { line, keys: [k.id] }),
              ],
            });
          });
        }
      }
    }
  },
};

const rules: readonly CheckRule[] = [
  ph01,
  ph02,
  ph04,
  ph05,
  ph06,
  ph07,
  ph08,
  ph09,
  ph10,
  ph11,
  ph12,
  ph13,
  ph14,
  ph17,
  ph18,
  ph19,
  ph20,
  ph21,
  ph22,
  ph23,
  ph24,
  ph25,
  ph26,
  ph27,
  ph28,
  ph29,
  ph30,
  ph31,
  ph32,
  ph33,
  ph34,
  ph35,
  ph36,
  ph37,
  ph38,
  ph39,
];

export default rules;
